import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileCsv, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

import { StandardURL } from "../App";

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [transactionCode, setTransactionCode] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalMpesaOpen, setIsModalMpesaOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null); // Renamed from selectedReservationId for consistency
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recordToUpdate, setRecordToUpdate] = useState(null);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false); // NEW
  const [loadingReminder, setLoadingReminder] = useState(false); // NEW
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false); // NEW

  useEffect(() => {
    // Only fetch if propertyId is present on mount (or change)
    if (propertyId) {
      fetchReservations();
    }
  }, [propertyId]);

  const fetchReservations = () => {
    const token = localStorage.getItem("token");

    if (token && propertyId) { // Added propertyId check
      axios
        .get(
          `${StandardURL}/iNyumba/user/reservations/viewAll/${propertyId}`, // Using StandardURL
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setReservations(response.data.response || []); // Ensure it handles null/undefined
          toast.success(`Reservations fetched for Property ID: ${propertyId}`);
        })
        .catch((error) => {
          console.error("Error fetching reservations:", error);
          toast.error("Failed to fetch reservations or Property ID is invalid.");
          setReservations([]);
        });
    } else if (token) {
        toast.error("Please enter a Property ID to search.");
    }
  };

  const handleInputChange = (e) => {
    setPropertyId(e.target.value);
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      fetchReservations();
    }
  };

  const handlePayment = (userId) => {
    const token = localStorage.getItem("token");

    axios
      .post(
        `${StandardURL}/iNyumba/user/payment/initiate-payment/${userId}`, // Using StandardURL
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success("Payment initiated successfully");
        setWebUrl(response.data.response);
        setShowIframe(true);
      })
      .catch((error) => {
        console.error("Error initiating payment:", error);
        toast.error(error.response?.data?.response || "Payment initiation failed.");
      });
  };

  const handleMpesaPayment = (userId, phoneNumber) => {
    const token = localStorage.getItem("token");

    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    axios
      .post(
        `${StandardURL}/iNyumba/user/mpesapayment/rentpaymentinitiatestk/${userId}`, // Using StandardURL
        { phoneNumber },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        setShowPhoneModal(false);
        setPhoneNumber(""); // Clear input after successful submission
      })
      .catch((error) => {
        console.error("Error initiating mpesa payment:", error);
        toast.error(error.response?.data?.response || "Mpesa payment failed.");
        setShowPhoneModal(false);
      });
  };

  const handleOpenPhoneModal = (record) => {
    setRecordToUpdate(record);
    setPhoneNumber(record.phone || ""); // Pre-fill if phone number is available
    setShowPhoneModal(true);
  };

  const handleCloseIframe = () => {
    setShowIframe(false);
    setWebUrl("");
  };

  // Helper function to handle downloads, avoids duplication
  const handleDownloadFile = (endpoint, fileType, postData = null) => {
    const fileExtension = fileType === "pdf" ? "pdf" : "csv";
    const fileName = postData ? `property_reservations_${propertyId}.${fileExtension}` : `all_reservations_records.${fileExtension}`;

    // Set up request config
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { format: fileType },
      responseType: "blob",
    };

    // Use a unified request for GET/POST
    const request = postData
        ? axios.post(endpoint, postData, config) // POST for property specific
        : axios.get(endpoint, config);          // GET for all time

    request
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Download successful");
      })
      .catch((error) => {
        console.error(`Error downloading ${fileExtension}:`, error);
        toast.error(`Error downloading file. Check console for details.`);
      });
  }

  const handleDownload = (fileType) => {
    if (!propertyId) {
        toast.error("Please enter a Property ID to download property reservations.");
        return;
    }
    const endpoint = `${StandardURL}/iNyumba/reservationsreports`; // Using StandardURL
    handleDownloadFile(endpoint, fileType, { propertyId });
  };

  const handleDownloadAll = (fileType) => {
    const endpoint = `${StandardURL}/iNyumba/reservationsreportsalltime`; // Using StandardURL
    handleDownloadFile(endpoint, fileType);
  };

  const handleManualUpdate = (userId) => {
    const token = localStorage.getItem("token");

    axios
      .post(
        `${StandardURL}/iNyumba/manualcallbacktenants/${userId}`, // Using StandardURL
        {
          transactioncode: transactionCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        toast.success("iPay manual update triggered successfully");
      })
      .catch((error) => {
        console.error("Error triggering manual iPay update:", error);
        toast.error(error.response?.data?.response || "Manual iPay update failed.");
      })
      .finally(() => {
        setIsModalOpen(false);
        setTransactionCode("");
      });
  };

  const handleManualMpesaUpdate = (userId) => {
    const token = localStorage.getItem("token");

    axios
      .post(
        `${StandardURL}/iNyumba/manualmpesacallbacktenants/${userId}`, // Using StandardURL
        {
          transactioncode: transactionCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        toast.success("Mpesa manual update triggered successfully");
      })
      .catch((error) => {
        console.error("Error triggering manual Mpesa update:", error);
        toast.error(error.response?.data?.response || "Manual Mpesa update failed.");
      })
      .finally(() => {
        setIsModalMpesaOpen(false);
        setTransactionCode("");
      });
  };

  // NEW FUNCTION: Send Eviction Notice
  const handleSendEvictionNotice = (userId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to continue? This will send the eviction notice."
    );

    if (isConfirmed) {
      const token = localStorage.getItem("token");

      if (token) {
        axios
          .post(
            `${StandardURL}/iNyumba/admin/noticelandlordtotenant/${userId}`, // Using StandardURL
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            console.log("Eviction notice sent successfully", response.data);
            toast.success("Eviction notice sent successfully!");
          })
          .catch((error) => {
            console.error("Error sending eviction notice:", error);
            toast.error("Failed to send eviction notice.");
          });
      }
    }
  };

  // NEW FUNCTION: Send Reminder
  const handleSendReminder = (type) => {
    const token = localStorage.getItem("token");
    if (!propertyId || !token) { // Use the component's propertyId state
        toast.error("Please search for a property first to send a reminder.");
        return;
    }

    setLoadingReminder(true);
    const url =
      type === "email"
        ? `${StandardURL}/iNyumba/sendbillremindersemail/${propertyId}` // Using StandardURL
        : `${StandardURL}/iNyumba/sendbillreminderssms/${propertyId}`; // Using StandardURL

    axios
      .post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success(res.data.response);
      })
      .catch((err) => {
        toast.error(err.response?.data?.response || "Failed to send reminder");
      })
      .finally(() => setLoadingReminder(false));
  };

  // NEW FUNCTION: Open Change Status Modal
  const handleChangeStatus = (userId) => {
    setSelectedUserId(userId);
    setShowChangeStatusModal(true);
  };

  // NEW FUNCTION: Confirm and Execute Change Status
  const confirmChangeStatus = () => {
    const token = localStorage.getItem("token");
    if (!selectedUserId || !token) return;

    setLoadingChangeStatus(true);

    axios
      .patch(
        `${StandardURL}/iNyumba/updatereservationstatusmanually/${selectedUserId}`, // Using StandardURL
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success("Status updated successfully!");
        setReservations((prev) =>
          prev.map((r) =>
            r.user_id === selectedUserId
              ? { ...r, payment_status: "PAID" } // Use 'PAID' to match existing statuses in reservation.js
              : r
          )
        );
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.response || "Failed to update reservation status"
        );
      })
      .finally(() => {
        setLoadingChangeStatus(false);
        setShowChangeStatusModal(false);
        setSelectedUserId(null);
      });
  };

  // Helper function to determine the color for status badges
  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "PAID":
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "CANCELED":
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6 border-b pb-2">
            Property Reservations Management
          </h1>

          {/* NEW: Reminder Buttons */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => handleSendReminder("email")}
              disabled={loadingReminder || !propertyId}
              className={`px-4 py-2 rounded-md text-white font-semibold ${
                loadingReminder || !propertyId
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loadingReminder ? "Sending..." : "Reminder via Email"}
            </button>
            <button
              onClick={() => handleSendReminder("sms")}
              disabled={loadingReminder || !propertyId}
              className={`px-4 py-2 rounded-md text-white font-semibold ${
                loadingReminder || !propertyId
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {loadingReminder ? "Sending..." : "Reminder via SMS"}
            </button>
          </div>

          {/* ----------------------------------- */}
          {/* SEARCH AND DOWNLOAD CONTROLS (Top) */}
          {/* ----------------------------------- */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 p-4 bg-white rounded-xl shadow-lg">
            {/* Download All Buttons */}
            <div className="flex space-x-3 mb-4 sm:mb-0">
                <span className="text-gray-600 font-semibold self-center hidden sm:block">All Records:</span>
                <button
                    onClick={() => handleDownloadAll("csv")}
                    className="flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition duration-200 shadow-md"
                    title="Download All Reservations (CSV)"
                >
                    <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                    CSV
                </button>
                <button
                    onClick={() => handleDownloadAll("pdf")}
                    className="flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition duration-200 shadow-md"
                    title="Download All Reservations (PDF)"
                >
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                    PDF
                </button>
            </div>

            {/* Property Search Input */}
            <div className="flex w-full sm:w-auto">
              <input
                type="text"
                value={propertyId}
                onChange={handleInputChange}
                onKeyPress={handleEnterKeyPress}
                placeholder="Enter Property ID to search..."
                className="border border-gray-300 rounded-l-lg py-2 px-4 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150"
              />
              <button
                onClick={fetchReservations}
                className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r-lg focus:outline-none transition duration-150 shadow-md"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                Search
              </button>
            </div>
          </div>

          {/* ----------------------------------- */}
          {/* PROPERTY DOWNLOAD BUTTONS */}
          {/* ----------------------------------- */}
          {propertyId && (
            <div className="flex space-x-3 mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-600 font-semibold self-center">Property ID: {propertyId}</span>
                <button
                    onClick={() => handleDownload("csv")}
                    className="flex items-center bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-3 rounded-lg text-xs transition duration-200 shadow-sm"
                    title={`Download Reservations for ${propertyId} (CSV)`}
                >
                    <FontAwesomeIcon icon={faFileCsv} className="mr-1" />
                    Download CSV
                </button>
                <button
                    onClick={() => handleDownload("pdf")}
                    className="flex items-center bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-3 rounded-lg text-xs transition duration-200 shadow-sm"
                    title={`Download Reservations for ${propertyId} (PDF)`}
                >
                    <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                    Download PDF
                </button>
            </div>
          )}


          {/* ----------------------------------- */}
          {/* RESERVATIONS GRID */}
          {/* ----------------------------------- */}
          {reservations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {reservations.map((reservation) => (
                <div
                  key={reservation.room_id}
                  className="bg-white shadow-xl rounded-xl p-6 border-t-4 border-blue-500 hover:shadow-2xl transition duration-300"
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">
                    {reservation.user_name}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>
                      <strong className="font-semibold text-gray-900">Email:</strong> {reservation.email}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-900">Phone:</strong> {reservation.phone}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-900">House No:</strong> {reservation.house_no}
                    </p>
                    <p>
                      <strong className="font-semibold text-gray-900">Entry Date:</strong> {reservation.entrydate}
                    </p>
                    <div className="flex items-center space-x-4 pt-2">
                        <span className="font-semibold text-gray-900">Booking:</span>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.booking_status)}`}>
                          {reservation.booking_status}
                        </span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <span className="font-semibold text-gray-900">Payment:</span>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.payment_status)}`}>
                          {reservation.payment_status}
                        </span>
                    </div>
                    <p>
                      <strong className="font-semibold text-gray-900">Deposit Paid:</strong> {reservation.deposit_paid.toLocaleString()}
                    </p>
                  </div>

                  {/* ACTION BUTTONS */}
                  {reservation.payment_status === "PENDING" && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handlePayment(reservation.user_id)}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Initiate iPay Payment"
                      >
                        iPay
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUserId(reservation.user_id);
                          setIsModalOpen(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Manual Callback for iPay"
                      >
                        ManualCbk iPay
                      </button>
                      <button
                        onClick={() => handleOpenPhoneModal(reservation)}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Initiate Mpesa STK Push"
                      >
                        Mpesa
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUserId(reservation.user_id);
                          setIsModalMpesaOpen(true);
                        }}
                        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Manual Callback for Mpesa"
                      >
                        ManualCbk Mpesa
                      </button>
                      {/* NEW: Change Status Button */}
                      <button
                        onClick={() => handleChangeStatus(reservation.user_id)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Manually Change Status to Paid"
                      >
                        Change Status
                      </button>
                    </div>
                  )}

                  {/* NEW: Eviction Notice Button (always available) */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button
                        onClick={() => handleSendEvictionNotice(reservation.user_id)}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-3 rounded-lg text-xs shadow-md transition duration-200"
                        title="Send Eviction Notice"
                    >
                        Send Eviction Notice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-white rounded-xl shadow-lg mt-8">
                <p className="text-xl text-gray-600">
                    {propertyId ? "No reservations found for this Property ID." : "Please enter a Property ID to view reservations."}
                </p>
            </div>
          )}


          {/* ----------------------------------- */}
          {/* IFRAME MODAL (FULL-SCREEN) */}
          {/* ----------------------------------- */}
          {showIframe && webUrl && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-95 z-50 flex flex-col items-center justify-center">
              <button
                onClick={handleCloseIframe}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-5 rounded-full mb-4 flex items-center shadow-lg transform hover:scale-105 transition duration-200"
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Close Payment Window
              </button>
              <iframe
                src={webUrl}
                className="w-[90vw] h-[90vh] border-2 border-gray-700 rounded-lg mx-auto shadow-2xl"
                title="Payment Iframe"
              ></iframe>
            </div>
          )}

          {/* ----------------------------------- */}
          {/* MANUAL CALLBACK MODAL (IPAY) */}
          {/* ----------------------------------- */}
          {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-40">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-800 mb-5 border-b pb-2">
                  Enter Transaction Code (iPay)
                </h2>
                <input
                  type="text"
                  value={transactionCode}
                  onChange={(e) => setTransactionCode(e.target.value)}
                  placeholder="Enter transaction code (e.g., A1B2C3D4)"
                  className="border border-gray-300 rounded-lg p-3 mb-6 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleManualUpdate(selectedUserId)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ----------------------------------- */}
          {/* MANUAL CALLBACK MODAL (MPESA) */}
          {/* ----------------------------------- */}
          {isModalMpesaOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-40">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-800 mb-5 border-b pb-2">
                  Enter Transaction Code (Mpesa)
                </h2>
                <input
                  type="text"
                  value={transactionCode}
                  onChange={(e) => setTransactionCode(e.target.value)}
                  placeholder="Enter Mpesa transaction code (e.g., MPESA123)"
                  className="border border-gray-300 rounded-lg p-3 mb-6 w-full focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setIsModalMpesaOpen(false)}
                    className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleManualMpesaUpdate(selectedUserId)}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ----------------------------------- */}
          {/* MPESA PHONE NUMBER MODAL */}
          {/* ----------------------------------- */}
          {showPhoneModal && (
            <div className="fixed inset-0 flex justify-center items-center bg-gray-900 bg-opacity-70 z-40">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-sm transform transition-all duration-300">
                <h2 className="text-2xl font-extrabold text-gray-800 mb-5">
                  Initiate Mpesa Payment
                </h2>
                <p className="text-gray-600 mb-4">
                    Enter the phone number to receive the STK push for user <strong>{recordToUpdate?.user_name}</strong>.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleMpesaPayment(
                      recordToUpdate.user_id,
                      phoneNumber
                    );
                  }}
                >
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="e.g., 254712345678"
                    className="border border-gray-300 rounded-lg p-3 text-base mb-6 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowPhoneModal(false)}
                      className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                    >
                      Send STK Push
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ----------------------------------- */}
          {/* Change Status Modal (NEW) */}
          {/* ----------------------------------- */}
          {showChangeStatusModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-40">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-800 mb-5 border-b pb-2">
                  Are you sure you want to change status?
                </h2>
                <p className="mb-6 text-gray-600">
                  Clicking <strong>Yes</strong> will delete the bill from the DB
                  and update the reservation Payment status to{" "}
                  <strong>PAID</strong>.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowChangeStatusModal(false)}
                    className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    No
                  </button>
                  <button
                    onClick={confirmChangeStatus}
                    disabled={loadingChangeStatus}
                    className={`py-2 px-4 rounded-lg transition duration-200 text-white font-semibold ${
                      loadingChangeStatus
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-yellow-600 hover:bg-yellow-700"
                    }`}
                  >
                    {loadingChangeStatus ? "Processing..." : "Yes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default ReservationsPage;